import React, { createContext, useContext, useState, useEffect } from 'react';
import UserSettingsService from '../services/userSettingsService';
import { useAuth } from './AuthContext';

const SettingsContext = createContext();

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  // Load settings when component mounts or user changes
  useEffect(() => {
    loadSettings();
  }, [currentUser]);

  // Set up real-time listener for authenticated users
  useEffect(() => {
    let unsubscribe = null;

    if (currentUser) {
      unsubscribe = UserSettingsService.subscribeToSettings((newSettings) => {
        setSettings(newSettings);
        setError(null);
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userSettings = await UserSettingsService.getUserSettings();
      setSettings(userSettings);
      
      // Migrate existing settings for authenticated users
      if (currentUser) {
        await UserSettingsService.migrateExistingSettings(currentUser.uid);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError(err.message);
      // Load local settings as fallback
      const localSettings = UserSettingsService.getLocalSettings();
      setSettings(localSettings);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      setError(null);
      const success = await UserSettingsService.updateSettings(updates);
      
      if (success) {
        // Update local state immediately for better UX
        setSettings(prev => ({ ...prev, ...updates }));
        return true;
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.message);
      return false;
    }
  };

  const getSetting = (key, defaultValue = null) => {
    if (!settings) return defaultValue;
    
    // Support dot notation for nested settings
    const keys = key.split('.');
    let value = settings;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return defaultValue;
      }
    }
    
    return value;
  };

  const setSetting = async (key, value) => {
    try {
      setError(null);
      const success = await UserSettingsService.setSetting(key, value);
      
      if (success) {
        // Update local state
        setSettings(prev => {
          const newSettings = { ...prev };
          const keys = key.split('.');
          let current = newSettings;
          
          for (let i = 0; i < keys.length - 1; i++) {
            if (!(keys[i] in current)) {
              current[keys[i]] = {};
            }
            current = current[keys[i]];
          }
          
          current[keys[keys.length - 1]] = value;
          return newSettings;
        });
        return true;
      } else {
        throw new Error('Failed to set setting');
      }
    } catch (err) {
      console.error('Error setting value:', err);
      setError(err.message);
      return false;
    }
  };

  const resetSettings = async () => {
    try {
      setError(null);
      const success = await UserSettingsService.resetToDefaults();
      
      if (success) {
        await loadSettings(); // Reload settings from server
        return true;
      } else {
        throw new Error('Failed to reset settings');
      }
    } catch (err) {
      console.error('Error resetting settings:', err);
      setError(err.message);
      return false;
    }
  };

  // Convenience methods for common settings
  const getTheme = () => getSetting('theme', 'dark');
  const setTheme = (theme) => setSetting('theme', theme);
  
  const getOddsFormat = () => getSetting('oddsFormat', 'american');
  const setOddsFormat = (format) => setSetting('oddsFormat', format);
  
  const getFavoriteTeams = () => getSetting('favoriteTeams', []);
  const addFavoriteTeam = (team) => {
    const favorites = getFavoriteTeams();
    if (!favorites.includes(team)) {
      setSetting('favoriteTeams', [...favorites, team]);
    }
  };
  const removeFavoriteTeam = (team) => {
    const favorites = getFavoriteTeams();
    setSetting('favoriteTeams', favorites.filter(t => t !== team));
  };

  const getNotificationSettings = () => ({
    pushNotifications: getSetting('pushNotifications', false),
    emailNotifications: getSetting('emailNotifications', false),
    favoriteTeamAlerts: getSetting('favoriteTeamAlerts', false),
    promoAlerts: getSetting('promoAlerts', true),
    chatNotifications: getSetting('chatNotifications', true)
  });

  const value = {
    // State
    settings,
    loading,
    error,
    
    // Methods
    updateSettings,
    getSetting,
    setSetting,
    resetSettings,
    loadSettings,
    
    // Convenience methods
    getTheme,
    setTheme,
    getOddsFormat,
    setOddsFormat,
    getFavoriteTeams,
    addFavoriteTeam,
    removeFavoriteTeam,
    getNotificationSettings,
    
    // Quick access to common settings
    theme: getTheme(),
    oddsFormat: getOddsFormat(),
    favoriteTeams: getFavoriteTeams(),
    notifications: getNotificationSettings()
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}